import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContextAWS';
import leagueApiService from '../services/leagueApi';

function JoinInvite() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [inviteData, setInviteData] = useState(null);
  const [league, setLeague] = useState(null);
  const [accepting, setAccepting] = useState(false);

  useEffect(() => {
    validateInvitation();
  }, []);

  const validateInvitation = async () => {
    try {
      setLoading(true);
      
      // Check for new format: ?code=XXX&league=YYY
      const code = searchParams.get('code');
      const leagueId = searchParams.get('league');
      
      if (code && leagueId) {
        console.log('Using new invite format with code:', code, 'and league:', leagueId);
        
        // New format - fetch league and find team by invite code
        try {
          const leagueResult = await leagueApiService.getLeague(leagueId);
          console.log('League result:', leagueResult);
          
          if (leagueResult.success && leagueResult.league) {
            // Parse teams
            let teams = leagueResult.league.teams || [];
            if (typeof teams === 'string') {
              teams = JSON.parse(teams);
            }
            
            // Find team with this invite code
            const invitedTeam = teams.find(t => t.inviteCode === code && t.status === 'invited');
            console.log('Found invited team:', invitedTeam);
            
            if (invitedTeam) {
              setLeague(leagueResult.league);
              setInviteData({
                leagueId: leagueId,
                teamName: invitedTeam.name,
                email: invitedTeam.ownerEmail,
                inviteCode: code,
                teamId: invitedTeam.id
              });
            } else {
              setError('Invitation not found or already accepted. Please contact the league commissioner.');
            }
          } else {
            setError('League not found. This invitation may be invalid.');
          }
        } catch (error) {
          console.error('Error validating new format invitation:', error);
          setError(`Failed to validate invitation: ${error.message}`);
        }
      } else {
        // Fall back to old format: ?token=XXX
        const token = searchParams.get('token');
        
        if (!token) {
          setError('Invalid invitation link. No token or code provided.');
          setLoading(false);
          return;
        }

        // Decode invitation token (old format)
        try {
          // Use atob for browser compatibility (Buffer is Node.js only)
          const decoded = JSON.parse(atob(token));
          
          console.log('Decoded invitation (old format):', decoded);
          
          // Check if invitation is expired (7 days)
          const inviteAge = Date.now() - decoded.timestamp;
          const sevenDays = 7 * 24 * 60 * 60 * 1000;
          
          if (inviteAge > sevenDays) {
            setError('This invitation has expired. Please request a new invitation from the league commissioner.');
            setLoading(false);
            return;
          }

          // Fetch league details
          console.log('Fetching league:', decoded.leagueId);
          const leagueResult = await leagueApiService.getLeague(decoded.leagueId);
          
          console.log('League result:', leagueResult);
          
          if (leagueResult.success && leagueResult.league) {
            setLeague(leagueResult.league);
            setInviteData(decoded);
          } else {
            setError('League not found. This invitation may be invalid.');
          }
        } catch (decodeError) {
          console.error('Error decoding token:', decodeError);
          console.error('Token value:', token);
          setError(`Invalid invitation token: ${decodeError.message}. Please check your invitation link.`);
        }
      }
    } catch (err) {
      console.error('Error validating invitation:', err);
      setError('Failed to validate invitation. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptInvitation = async () => {
    if (!user) {
      // Save invitation parameters to localStorage so we can use them after login
      const code = searchParams.get('code');
      const leagueId = searchParams.get('league');
      const token = searchParams.get('token');
      
      if (code && leagueId) {
        localStorage.setItem('pendingInviteCode', code);
        localStorage.setItem('pendingInviteLeague', leagueId);
        navigate(`/register?redirect=/join-invite&code=${code}&league=${leagueId}`);
      } else if (token) {
        localStorage.setItem('pendingInvite', token);
        navigate('/register?redirect=/join-invite&token=' + token);
      }
      return;
    }

    try {
      setAccepting(true);
      setError('');
      
      const userId = user.userId || user.id || user.sub;
      
      console.log('Accepting invitation for user:', userId);
      console.log('League data:', league);
      console.log('Invite data:', inviteData);
      
      // Parse teams if they're a string
      let teams = league?.teams || [];
      if (typeof teams === 'string') {
        teams = JSON.parse(teams);
      }
      
      console.log('Teams:', teams);
      
      // Check if user already in league
      if (teams.some(t => t.owner === userId)) {
        setError('You are already a member of this league!');
        setAccepting(false);
        return;
      }

      // Find the invited team and update with actual user info
      const invitedTeam = teams.find(t => 
        t.id === inviteData.teamId || // New format uses teamId
        t.ownerEmail === inviteData.email || 
        t.owner === inviteData.email ||
        t.inviteCode === inviteData.inviteCode // Match by invite code
      );
      
      console.log('Found invited team:', invitedTeam);
      
      if (invitedTeam) {
        // Update the invited team with actual user info
        const userFullName = user.fullName || user.name || `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email || user.username;
        invitedTeam.owner = userId;
        invitedTeam.ownerName = userFullName;
        invitedTeam.status = 'active';
        delete invitedTeam.invitedAt;
        delete invitedTeam.inviteCode; // Clear invite code to prevent reuse
        
        console.log('Updating league with teams:', teams);
        
        // Update league with new team info
        const result = await leagueApiService.updateLeague(league.leagueId || league.id, {
          teams: teams
        });
        
        console.log('Update result:', result);
      } else {
        // Team was removed, add as new team
        console.log('Invited team not found, joining as new team');
        
        const userFullName = user.fullName || user.name || `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email || user.username;
        const result = await leagueApiService.joinLeague(league.leagueId || league.id, {
          name: inviteData.teamName,
          owner: userId,
          ownerName: userFullName,
          logoEmoji: '⚾',
          logoColor: '#007bff'
        });
        
        console.log('Join result:', result);
      }

      alert('✅ Successfully joined the league!');
      navigate(`/league/${league.leagueId || league.id}/roster`);
    } catch (err) {
      console.error('Error accepting invitation:', err);
      console.error('Error details:', {
        message: err.message,
        stack: err.stack,
        name: err.name
      });
      setError(`Failed to join league: ${err.message || 'Unknown error'}. Check console for details.`);
    } finally {
      setAccepting(false);
    }
  };

  if (loading) {
    return (
      <div className="container" style={{ maxWidth: '600px', marginTop: '50px' }}>
        <div className="empty-state">
          <div className="spinner" style={{ margin: '0 auto 12px' }} />
          <h2 className="empty-state-title">Loading Invitation…</h2>
          <p className="empty-state-desc">Please wait while we verify your invitation.</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container" style={{ maxWidth: '600px', marginTop: '50px' }}>
        <div className="card">
          <h2 className="text-card-title" style={{ marginBottom: '16px' }}>❌ Invalid Invitation</h2>
          <div className="alert alert-danger" style={{ marginBottom: '20px' }}>
            {error}
          </div>
          <button
            className="btn btn-primary"
            onClick={() => navigate('/')}
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  if (!inviteData || !league) {
    return (
      <div className="container" style={{ maxWidth: '600px', marginTop: '50px' }}>
        <div className="empty-state">
          <div className="empty-state-icon">🔍</div>
          <h2 className="empty-state-title">No Invitation Found</h2>
          <button className="btn btn-primary" onClick={() => navigate('/')}>
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="container" style={{ maxWidth: '700px', marginTop: '50px' }}>
      <div className="card">
        <div style={{ textAlign: 'center', marginBottom: '30px' }}>
          <h1 className="text-title">⚾ League Invitation</h1>
          <p className="text-secondary">
            You've been invited to join a fantasy baseball league!
          </p>
        </div>

        <div style={{
          padding: '25px',
          backgroundColor: 'var(--surface-muted)',
          borderRadius: 'var(--radius-md)',
          border: '1px solid var(--border)',
          marginBottom: '30px'
        }}>
          <h2 className="text-card-title" style={{ marginTop: 0, color: 'var(--primary)' }}>🏆 {league.name}</h2>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'auto 1fr',
            gap: '10px 20px',
            marginTop: '20px'
          }}>
            <strong>Your Team:</strong>
            <span>{inviteData.teamName}</span>
            
            <strong>Season:</strong>
            <span>{league.season?.year || 2026}</span>
            
            <strong>Teams:</strong>
            <span>{league.teams?.length || 0} / {league.settings?.maxTeams || 12}</span>
            
            <strong>Scoring:</strong>
            <span>{league.settings?.scoringType || 'Standard'}</span>
            
            {league.draftDate && (
              <>
                <strong>Draft Date:</strong>
                <span>{new Date(league.draftDate).toLocaleDateString()} at {league.draftTime}</span>
              </>
            )}
          </div>
        </div>

        {!user ? (
          <div>
            <div className="alert alert-warning" style={{ marginBottom: '20px' }}>
              ℹ️ You need to sign in or create an account to accept this invitation.
            </div>

            <div className="card-actions" style={{ justifyContent: 'center' }}>
              <button
                className="btn btn-primary"
                style={{ flex: 1 }}
                onClick={() => {
                  const code = searchParams.get('code');
                  const leagueId = searchParams.get('league');
                  const token = searchParams.get('token');
                  if (code && leagueId) {
                    localStorage.setItem('pendingInviteCode', code);
                    localStorage.setItem('pendingInviteLeague', leagueId);
                    navigate(`/login?redirect=/join-invite&code=${code}&league=${leagueId}`);
                  } else if (token) {
                    localStorage.setItem('pendingInvite', token);
                    navigate('/login?redirect=/join-invite&token=' + token);
                  }
                }}
              >
                Sign In
              </button>
              <button
                className="btn btn-secondary"
                style={{ flex: 1 }}
                onClick={() => {
                  const code = searchParams.get('code');
                  const leagueId = searchParams.get('league');
                  const token = searchParams.get('token');
                  if (code && leagueId) {
                    localStorage.setItem('pendingInviteCode', code);
                    localStorage.setItem('pendingInviteLeague', leagueId);
                    navigate(`/register?redirect=/join-invite&code=${code}&league=${leagueId}`);
                  } else if (token) {
                    localStorage.setItem('pendingInvite', token);
                    navigate('/register?redirect=/join-invite&token=' + token);
                  }
                }}
              >
                Create Account
              </button>
            </div>
          </div>
        ) : (
          <div>
            <div className="alert alert-success" style={{ marginBottom: '20px', textAlign: 'center' }}>
              ✅ You're signed in as <strong>{user.email}</strong>
            </div>

            <div className="card-actions">
              <button
                className="btn btn-primary"
                onClick={handleAcceptInvitation}
                disabled={accepting}
                style={{ flex: 1 }}
              >
                {accepting ? 'Joining...' : '✅ Accept & Join League'}
              </button>
              <button
                className="btn btn-secondary"
                onClick={() => navigate('/')}
              >
                Decline
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default JoinInvite;

